/**
 * 画板指针交互：命中检测 / 键盘删除 / 地图事件绑定
 */
import { useCallback, useEffect } from 'react';
import { message } from '../utils/antdMessage';
import { whenMapStyleSettled } from '../utils/dashboard/earthDeepSpace';
import {
  LINE_LAYER,
  FILL_LAYER,
  POINT_LAYER,
  DEFAULT_TEXT_STYLE,
  DRAFT_TOOLS,
  clickToWgs84,
  pathLengthMeters,
  polygonAreaMeters,
  formatDistance,
  dedupeConsecutiveCoords,
  formatArea,
  openRingCoords,
  uid,
  findPolylineHit,
  withLineMeta,
  withAreaMeta,
  isLineFeatureType,
  isAreaFeatureType,
  isSketchFeatureType,
  isTextFeatureType,
  isEditableFeatureType,
  applyMapToolCursor,
  cursorForTool,
  normalizeScreenBox,
  collectFeaturesInScreenBox,
  updateBoxSelectOverlay
} from '../utils/mapDrawingBoard';

export default function useMapDrawingPointer({
  mapReady,
  getMap,
  onStartInteract,
  pushOpen,
  ensureLayers,
  syncFeaturesToMap,
  syncPreview,
  clearPreview,
  clearMeasureMarkers,
  clearTextMarkers,
  finishDraft,
  deleteSelectedFeature,
  setSelectedId,
  setSelectedIds,
  handlersRef,
  featuresRef,
  draftRef,
  toolRef,
  dragRef,
  suppressClickRef,
  selectedIdRef,
  selectedIdsRef,
  sketchStrokeRef,
  boxSelectRef,
  editingIdRef,
  colorRef,
  opacityRef,
  sketchWidthRef,
  textStyleRef,
  textContentRef,
  setDraftPoints,
  setFeatures,
  setDrawColor,
  setDrawOpacity,
  setSketchLineWidth,
  setTextContent,
  setTextEditOpen,
  setExpanded,
  setPinned,
  setStatusText,
  setCtxMenu
}) {
  const hitFeatureId = useCallback((map, point, pad = 12) => {
    if (!map) return null;
    const layers = [LINE_LAYER, FILL_LAYER, POINT_LAYER].filter((id) => map.getLayer(id));
    if (!layers.length) return null;
    const bbox = [
      [point.x - pad, point.y - pad],
      [point.x + pad, point.y + pad]
    ];
    const hits = map.queryRenderedFeatures(bbox, { layers });
    if (!hits.length) return null;
    const preferred = hits.find((h) => h.properties?.kind !== 'vertex') || hits[0];
    return preferred.properties?.id || null;
  }, []);

  // Del / Backspace：删除已定型选中图形
  useEffect(() => {
    if (pushOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (DRAFT_TOOLS.includes(toolRef.current) && draftRef.current.length > 0) return;
      if (!selectedIdsRef.current.length) return;
      e.preventDefault();
      deleteSelectedFeature();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pushOpen, deleteSelectedFeature]);

  // 绑定地图事件
  useEffect(() => {
    if (!mapReady) return undefined;
    const map = getMap?.();
    if (!map) return undefined;

    const attach = () => {
      ensureLayers(map);
      syncFeaturesToMap(map, featuresRef.current);

      const applyDraftPoints = (pts, kind, status) => {
        setDraftPoints(pts);
        draftRef.current = pts;
        syncPreview(map, pts, kind);
        if (status) setStatusText(status);
      };

      const updateFeatureVertex = (featureId, index, wgsPt) => {
        const next = featuresRef.current.map((f) => {
          if (f.id !== featureId) return f;
          if (isLineFeatureType(f.type)) {
            const coords = f.coordinates.map((c) => [...c]);
            if (index < 0 || index >= coords.length) return f;
            coords[index] = [wgsPt[0], wgsPt[1]];
            return withLineMeta(f, coords);
          }
          if (isAreaFeatureType(f.type)) {
            const coords = openRingCoords(f.coordinates).map((c) => [...c]);
            if (index < 0 || index >= coords.length) return f;
            coords[index] = [wgsPt[0], wgsPt[1]];
            return withAreaMeta(f, coords);
          }
          return f;
        });
        setFeatures(next);
        featuresRef.current = next;
        syncFeaturesToMap(map, next);
      };

      const onMouseDown = (e) => {
        const currentTool = toolRef.current;
        if (!currentTool) return;
        if (e.originalEvent?.button != null && e.originalEvent.button !== 0) return;

        // 自由绘画：点在已有线条上则选中；空白处按下开始描边
        if (currentTool === 'sketch') {
          const hitId = hitFeatureId(map, e.point, 14);
          if (hitId) {
            e.preventDefault();
            onStartInteract?.();
            const feat = featuresRef.current.find((f) => f.id === hitId);
            setSelectedId(hitId);
            if (feat) {
              setDrawColor(feat.color);
              setDrawOpacity(feat.opacity);
              if (feat.lineWidth) setSketchLineWidth(feat.lineWidth);
            }
            setExpanded(true);
            setStatusText(`已选中：${feat?.name || '手绘'}（可改颜色/线宽；Del 删除）`);
            suppressClickRef.current = true;
            return;
          }
          e.preventDefault();
          onStartInteract?.();
          const pt = clickToWgs84(e.lngLat);
          sketchStrokeRef.current = {
            active: true,
            points: [pt],
            lastScreen: { x: e.point.x, y: e.point.y }
          };
          draftRef.current = [pt];
          setDraftPoints([pt]);
          syncPreview(map, [pt], 'sketch');
          try { map.dragPan.disable(); } catch (_) { /* ignore */ }
          applyMapToolCursor(map, 'sketch');
          return;
        }

        // 选择工具：优先拖顶点；否则开始框选
        if (currentTool === 'select') {
          const id = selectedIdRef.current || hitFeatureId(map, e.point);
          const feat = id ? featuresRef.current.find((f) => f.id === id) : null;
          if (
            feat
            && isEditableFeatureType(feat.type)
            && !isSketchFeatureType(feat.type)
            && !isTextFeatureType(feat.type)
          ) {
            const ring = isAreaFeatureType(feat.type)
              ? openRingCoords(feat.coordinates)
              : feat.coordinates;
            const hit = findPolylineHit(map, ring, e.point, {
              closed: isAreaFeatureType(feat.type) && ring.length >= 3
            });
            if (hit?.type === 'vertex') {
              e.preventDefault();
              onStartInteract?.();
              setSelectedId(feat.id);
              dragRef.current = {
                kind: 'feature',
                featureId: feat.id,
                index: hit.index,
                moved: false
              };
              try { map.dragPan.disable(); } catch (_) { /* ignore */ }
              map.getCanvas().style.cursor = 'grabbing';
              return;
            }
          }
          e.preventDefault();
          onStartInteract?.();
          boxSelectRef.current = {
            active: true,
            start: { x: e.point.x, y: e.point.y },
            current: { x: e.point.x, y: e.point.y },
            moved: false,
            additive: !!(e.originalEvent?.shiftKey)
          };
          try { map.dragPan.disable(); } catch (_) { /* ignore */ }
          map.getCanvas().style.cursor = 'crosshair';
          updateBoxSelectOverlay(map, normalizeScreenBox(
            boxSelectRef.current.start,
            boxSelectRef.current.current
          ));
          return;
        }

        // 草稿中：按住顶点拖拽
        if (['route', 'measure', 'polygon'].includes(currentTool) && draftRef.current.length > 0) {
          const hit = findPolylineHit(map, draftRef.current, e.point, {
            closed: currentTool === 'polygon' && draftRef.current.length >= 3
          });
          if (hit?.type === 'vertex') {
            e.preventDefault();
            onStartInteract?.();
            dragRef.current = { kind: 'draft', index: hit.index, moved: false };
            try { map.dragPan.disable(); } catch (_) { /* ignore */ }
            map.getCanvas().style.cursor = 'grabbing';
            return;
          }
        }

        // 选中工具外的空闲绘制工具：拖已定型图形顶点（不含手绘/文字/选择框）
        const canDragFinished =
          ['route', 'measure', 'polygon', 'rectangle'].includes(currentTool)
          && draftRef.current.length === 0;
        if (canDragFinished) {
          const id = selectedIdRef.current || hitFeatureId(map, e.point);
          const feat = id ? featuresRef.current.find((f) => f.id === id) : null;
          if (
            feat
            && isEditableFeatureType(feat.type)
            && !isSketchFeatureType(feat.type)
            && !isTextFeatureType(feat.type)
          ) {
            const ring = isAreaFeatureType(feat.type)
              ? openRingCoords(feat.coordinates)
              : feat.coordinates;
            const hit = findPolylineHit(map, ring, e.point, {
              closed: isAreaFeatureType(feat.type) && ring.length >= 3
            });
            if (hit?.type === 'vertex') {
              e.preventDefault();
              onStartInteract?.();
              setSelectedId(feat.id);
              dragRef.current = {
                kind: 'feature',
                featureId: feat.id,
                index: hit.index,
                moved: false
              };
              try { map.dragPan.disable(); } catch (_) { /* ignore */ }
              map.getCanvas().style.cursor = 'grabbing';
            }
          }
        }
      };

      const finishSketchStroke = () => {
        const stroke = sketchStrokeRef.current;
        if (!stroke?.active) return false;
        sketchStrokeRef.current = null;
        try { map.dragPan.enable(); } catch (_) { /* ignore */ }
        applyMapToolCursor(map, toolRef.current);
        suppressClickRef.current = true;
        const pts = dedupeConsecutiveCoords(stroke.points || [], 0.3);
        clearPreview();
        if (pts.length < 2) {
          setStatusText('笔画太短，请重新按住拖动绘画');
          return true;
        }
        const feature = {
          id: uid('sketch'),
          type: 'sketch',
          coordinates: pts.map((p) => [...p]),
          color: colorRef.current,
          opacity: opacityRef.current,
          lineWidth: sketchWidthRef.current || 3,
          fill: false,
          name: `手绘 ${featuresRef.current.filter((f) => f.type === 'sketch').length + 1}`,
          meta: {}
        };
        const next = [...featuresRef.current, feature];
        setFeatures(next);
        featuresRef.current = next;
        syncFeaturesToMap(map, next);
        setSelectedId(feature.id);
        setStatusText('手绘已完成，可选中改颜色/线宽或 Del 删除');
        message.success('手绘已完成');
        return true;
      };

      const onMouseMove = (e) => {
        // 框选拖动
        const boxSel = boxSelectRef.current;
        if (boxSel?.active) {
          const dx = e.point.x - boxSel.start.x;
          const dy = e.point.y - boxSel.start.y;
          if (Math.hypot(dx, dy) > 4) boxSel.moved = true;
          boxSel.current = { x: e.point.x, y: e.point.y };
          updateBoxSelectOverlay(map, normalizeScreenBox(boxSel.start, boxSel.current));
          return;
        }

        // 自由绘画描边中
        const stroke = sketchStrokeRef.current;
        if (stroke?.active) {
          const dx = e.point.x - stroke.lastScreen.x;
          const dy = e.point.y - stroke.lastScreen.y;
          if (Math.hypot(dx, dy) < 2.5) return;
          stroke.lastScreen = { x: e.point.x, y: e.point.y };
          const pt = clickToWgs84(e.lngLat);
          stroke.points.push(pt);
          draftRef.current = stroke.points;
          syncPreview(map, stroke.points, 'sketch');
          return;
        }

        const drag = dragRef.current;
        if (!drag) {
          const currentTool = toolRef.current;
          if (currentTool === 'sketch') {
            const hitId = hitFeatureId(map, e.point, 14);
            map.getCanvas().style.cursor = hitId ? 'pointer' : cursorForTool('sketch');
            return;
          }
          if (currentTool === 'select') {
            applyMapToolCursor(map, 'select');
            return;
          }
          if (['route', 'measure', 'polygon'].includes(currentTool) && draftRef.current.length >= 2) {
            const hit = findPolylineHit(map, draftRef.current, e.point, {
              closed: currentTool === 'polygon' && draftRef.current.length >= 3
            });
            if (hit) {
              map.getCanvas().style.cursor = hit.type === 'vertex' ? 'grab' : 'copy';
            } else {
              applyMapToolCursor(map, currentTool);
            }
            return;
          }
          applyMapToolCursor(map, currentTool);
          return;
        }
        const wgs = clickToWgs84(e.lngLat);
        drag.moved = true;
        if (drag.kind === 'draft') {
          const pts = draftRef.current.map((c) => [...c]);
          if (drag.index >= 0 && drag.index < pts.length) {
            pts[drag.index] = wgs;
            draftRef.current = pts;
            setDraftPoints(pts);
            syncPreview(map, pts, toolRef.current);
          }
        } else if (drag.kind === 'feature' && drag.featureId) {
          updateFeatureVertex(drag.featureId, drag.index, wgs);
        }
      };

      const finishBoxSelect = () => {
        const boxSel = boxSelectRef.current;
        if (!boxSel?.active) return false;
        boxSelectRef.current = null;
        updateBoxSelectOverlay(map, null);
        try { map.dragPan.enable(); } catch (_) { /* ignore */ }
        applyMapToolCursor(map, toolRef.current);
        suppressClickRef.current = true;

        if (!boxSel.moved) {
          // 单击：点选单个
          const id = hitFeatureId(map, boxSel.current, 12);
          if (!id) {
            if (!boxSel.additive) setSelectedIds([]);
            setStatusText('框选：拖动画框可选中多个图形；单击选中单个');
            return true;
          }
          if (boxSel.additive) {
            const cur = selectedIdsRef.current;
            setSelectedIds(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
          } else {
            setSelectedId(id);
          }
          const feat = featuresRef.current.find((f) => f.id === id);
          if (feat) {
            setDrawColor(feat.color);
            setDrawOpacity(feat.opacity);
            if (feat.lineWidth) setSketchLineWidth(feat.lineWidth);
            setExpanded(true);
            setStatusText(`已选中：${feat.name || feat.type}（可换色/编辑/Del 删除；Shift+框选可追加）`);
          }
          return true;
        }

        const box = normalizeScreenBox(boxSel.start, boxSel.current);
        if (box.maxX - box.minX < 6 || box.maxY - box.minY < 6) {
          return true;
        }
        const hits = collectFeaturesInScreenBox(map, featuresRef.current, box);
        const hitIds = hits.map((f) => f.id);
        if (!hitIds.length) {
          if (!boxSel.additive) setSelectedIds([]);
          setStatusText('框选区域内没有图形');
          message.info('框选区域内没有图形');
          return true;
        }
        if (boxSel.additive) {
          const merged = Array.from(new Set([...selectedIdsRef.current, ...hitIds]));
          setSelectedIds(merged);
        } else {
          setSelectedIds(hitIds);
        }
        const first = hits[0];
        if (first) {
          setDrawColor(first.color);
          setDrawOpacity(first.opacity);
        }
        setExpanded(true);
        setPinned(true);
        setStatusText(`已框选 ${hitIds.length} 项：可批量换色/删除；单项可再编辑`);
        message.success(`已框选 ${hitIds.length} 个图形`);
        return true;
      };

      const endDrag = () => {
        if (finishBoxSelect()) return;
        if (finishSketchStroke()) return;
        const drag = dragRef.current;
        if (!drag) return;
        if (drag.moved) suppressClickRef.current = true;
        dragRef.current = null;
        try { map.dragPan.enable(); } catch (_) { /* ignore */ }
        applyMapToolCursor(map, toolRef.current);
        if (drag.kind === 'draft' && draftRef.current.length >= 2) {
          const kind = toolRef.current;
          if (kind === 'measure') {
            setStatusText(`已调整顶点，当前 ${formatDistance(pathLengthMeters(draftRef.current))}`);
          } else {
            setStatusText('已调整顶点（右键或「完成」定型）');
          }
        } else if (drag.kind === 'feature') {
          setStatusText('已拖动顶点修改路径');
        }
      };

      const onClick = (e) => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        const currentTool = toolRef.current;
        if (!currentTool) return;
        e.preventDefault?.();
        onStartInteract?.();

        // 文字工具：点击放置
        if (currentTool === 'text') {
          const pt = clickToWgs84(e.lngLat);
          const style = textStyleRef.current || DEFAULT_TEXT_STYLE;
          const content = (textContentRef.current || '').trim() || '请输入说明文字';
          const feature = {
            id: uid('text'),
            type: 'text',
            coordinates: [pt],
            text: content,
            fontFamily: style.fontFamily,
            fontSize: style.fontSize,
            letterSpacing: style.letterSpacing,
            lineHeight: style.lineHeight,
            color: style.color || colorRef.current,
            bgColor: style.bgColor,
            bgEnabled: style.bgEnabled !== false,
            opacity: style.opacity ?? opacityRef.current,
            fill: false,
            name: content.slice(0, 12) || '文字',
            meta: {}
          };
          const next = [...featuresRef.current, feature];
          setFeatures(next);
          featuresRef.current = next;
          syncFeaturesToMap(map, next);
          setSelectedId(feature.id);
          setDrawColor(feature.color);
          setDrawOpacity(feature.opacity);
          setTextContent(feature.text);
          setExpanded(true);
          setPinned(true);
          setTextEditOpen(true);
          setStatusText('文字已放置：可在侧栏改内容与样式，或拖动位置');
          message.success('文字已放置');
          return;
        }

        // 自由绘画：点选已在 mousedown 处理；单击空白忽略
        if (currentTool === 'sketch') return;

        if (currentTool === 'select' || currentTool === 'eraser' || currentTool === 'fill') {
          // 选择工具的点选/框选已在 mousedown/mouseup 完成
          if (currentTool === 'select') return;
          const id = hitFeatureId(map, e.point);
          if (!id) {
            return;
          }
          if (currentTool === 'eraser') {
            const next = featuresRef.current.filter((f) => f.id !== id);
            setFeatures(next);
            featuresRef.current = next;
            syncFeaturesToMap(map, next);
            if (selectedIdsRef.current.includes(id)) {
              setSelectedIds(selectedIdsRef.current.filter((x) => x !== id));
            }
            message.success('已删除');
            return;
          }
          if (currentTool === 'fill') {
            const next = featuresRef.current.map((f) => {
              if (f.id !== id) return f;
              if (f.type !== 'polygon' && f.type !== 'rectangle') {
                message.info('仅区域/矩形可填充');
                return f;
              }
              return { ...f, fill: !f.fill };
            });
            setFeatures(next);
            featuresRef.current = next;
            syncFeaturesToMap(map, next);
            setSelectedId(id);
            return;
          }
          return;
        }

        if (currentTool === 'rectangle' && !editingIdRef.current) {
          if (draftRef.current.length === 0) {
            const id = hitFeatureId(map, e.point);
            const feat = id ? featuresRef.current.find((f) => f.id === id) : null;
            if (feat && isEditableFeatureType(feat.type)) {
              setSelectedId(feat.id);
              setDrawColor(feat.color);
              setDrawOpacity(feat.opacity);
              setExpanded(true);
              setStatusText(`已选中：${feat.name}（侧栏可「编辑」；Del 删除）`);
              return;
            }
          }
          const pt = clickToWgs84(e.lngLat);
          const pts = [...draftRef.current, pt];
          if (pts.length === 1) {
            applyDraftPoints(pts, 'rectangle', '再点击对角点完成矩形（ESC 退一点 / 长按清空）');
          } else {
            draftRef.current = pts.slice(0, 2);
            setDraftPoints(draftRef.current);
            finishDraft();
          }
          return;
        }

        if (['route', 'measure', 'polygon'].includes(currentTool)) {
          const pts = draftRef.current;

          // 绘制工具且无草稿：点已定型图形 → 选中并展开侧栏
          if (pts.length === 0) {
            const id = hitFeatureId(map, e.point);
            const feat = id ? featuresRef.current.find((f) => f.id === id) : null;
            if (feat && isEditableFeatureType(feat.type)) {
              setSelectedId(feat.id);
              setDrawColor(feat.color);
              setDrawOpacity(feat.opacity);
              setExpanded(true);
              setStatusText(`已选中：${feat.name}（侧栏可「编辑」；Del 删除）`);
              return;
            }
          }

          // 点在已有草稿边上 → 插入顶点；点在顶点上提示拖拽
          if (pts.length >= 2) {
            const hit = findPolylineHit(map, pts, e.point, {
              closed: currentTool === 'polygon' && pts.length >= 3
            });
            if (hit?.type === 'vertex') {
              setStatusText(`顶点 ${hit.index + 1}：按住拖动可改形状`);
              return;
            }
            if (hit?.type === 'edge' && hit.wgs) {
              const next = [
                ...pts.slice(0, hit.index + 1),
                hit.wgs,
                ...pts.slice(hit.index + 1)
              ];
              let tip;
              if (currentTool === 'measure' && next.length >= 2) {
                tip = `已在线段中插入点，当前 ${formatDistance(pathLengthMeters(next))}`;
              } else if (currentTool === 'polygon' && next.length >= 3) {
                tip = `已插入顶点，当前面积 ${formatArea(polygonAreaMeters(next))}`;
              } else {
                tip = `已在边上插入第 ${hit.index + 2} 点（共 ${next.length} 点）`;
              }
              applyDraftPoints(next, currentTool, tip);
              return;
            }
          }

          const pt = clickToWgs84(e.lngLat);
          const next = [...pts, pt];
          let statusMsg;
          if (currentTool === 'measure' && next.length >= 2) {
            statusMsg = `当前折线 ${formatDistance(pathLengthMeters(next))}（点在线上可插点；拖顶点；双击/右键完成）`;
          } else if (currentTool === 'polygon' && next.length >= 3) {
            statusMsg = `已添加 ${next.length} 点，面积 ${formatArea(polygonAreaMeters(next))}（双击/右键完成）`;
          } else {
            statusMsg = `已添加 ${next.length} 点（点在边上可插点；拖顶点；双击/右键完成）`;
          }
          applyDraftPoints(next, currentTool, statusMsg);
        }
      };

      const onDblClick = (e) => {
        const currentTool = toolRef.current;
        if (!currentTool || !['route', 'measure', 'polygon'].includes(currentTool)) return;
        e.preventDefault();
        finishDraft();
      };

      const onContextMenu = (e) => {
        const currentTool = toolRef.current;
        // 绘制中右键 = 完成
        if (currentTool && ['route', 'measure', 'polygon'].includes(currentTool) && draftRef.current.length >= 2) {
          e.preventDefault();
          finishDraft();
          return;
        }
        const id = hitFeatureId(map, e.point);
        if (!id) {
          setCtxMenu(null);
          return;
        }
        const feat = featuresRef.current.find((f) => f.id === id);
        if (!feat || !isEditableFeatureType(feat.type)) {
          setCtxMenu(null);
          return;
        }
        e.preventDefault();
        setSelectedId(id);
        setCtxMenu({
          x: e.originalEvent.clientX,
          y: e.originalEvent.clientY,
          featureId: id
        });
      };

      handlersRef.current = {
        onClick,
        onDblClick,
        onContextMenu,
        onMouseDown,
        onMouseMove,
        onMouseUp: endDrag,
        onMouseLeave: endDrag
      };
      map.on('click', onClick);
      map.on('dblclick', onDblClick);
      map.on('contextmenu', onContextMenu);
      map.on('mousedown', onMouseDown);
      map.on('mousemove', onMouseMove);
      map.on('mouseup', endDrag);
      map.getCanvas().addEventListener('mouseleave', endDrag);
    };

    if (map.isStyleLoaded()) attach();
    else map.once('load', attach);

    const rehydrateDrawLayers = () => {
      try { map.__zhxtPreviewLineV2 = false; } catch (_) { /* ignore */ }
      let attempts = 0;
      const tryRestore = () => {
        attempts += 1;
        const ok = ensureLayers(map);
        if (ok) {
          syncFeaturesToMap(map, featuresRef.current);
          if (
            ['route', 'measure', 'polygon', 'rectangle', 'sketch'].includes(toolRef.current) &&
            draftRef.current.length >= 1
          ) {
            syncPreview(map, draftRef.current, toolRef.current);
          }
          return;
        }
        // 栅格底图过渡期可能仍抛 Style is not done loading，短时重试
        if (attempts < 12) {
          setTimeout(tryRestore, 200);
        } else {
          console.warn('画板图层在底图切换后未能重建，请再点一次画板工具');
        }
      };
      tryRestore();
    };

    const onStyle = () => {
      // style.load 后等 idle/超时再重建，并带重试（覆盖慢速栅格底图）
      whenMapStyleSettled(map, rehydrateDrawLayers, 1800);
    };
    map.on('style.load', onStyle);

    return () => {
      const {
        onClick,
        onDblClick,
        onContextMenu,
        onMouseDown,
        onMouseMove,
        onMouseUp,
        onMouseLeave
      } = handlersRef.current || {};
      try {
        if (onClick) map.off('click', onClick);
        if (onDblClick) map.off('dblclick', onDblClick);
        if (onContextMenu) map.off('contextmenu', onContextMenu);
        if (onMouseDown) map.off('mousedown', onMouseDown);
        if (onMouseMove) map.off('mousemove', onMouseMove);
        if (onMouseUp) map.off('mouseup', onMouseUp);
        if (onMouseLeave) map.getCanvas().removeEventListener('mouseleave', onMouseLeave);
        map.off('style.load', onStyle);
        map.dragPan.enable();
      } catch (_) { /* ignore */ }
      clearMeasureMarkers();
      clearTextMarkers();
      updateBoxSelectOverlay(map, null);
    };
  }, [mapReady, getMap, ensureLayers, syncFeaturesToMap, syncPreview, finishDraft, hitFeatureId, onStartInteract, clearMeasureMarkers, clearPreview, clearTextMarkers, setSelectedId, setSelectedIds]);
}
